import React, { useRef, useState } from "react";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { Message } from "primereact/message";
import { SelectButton } from "primereact/selectbutton";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";

import useWallboxCatalogue from "../../../hooks/useWallboxCatalogue";
import { WallboxEntry } from "../../../types/onboarding";
import { downloadCatalogueCsv } from "../../../utils/mappingProposal";
import ImportMappingsDialog from "./components/ImportMappingsDialog";
import WallboxEntryDialog from "./components/WallboxEntryDialog";

// SOPS-169: which entries still need eRound's names. "Shown without mapping"
// are models customers can order today that eRound cannot set up yet.
type MappingFilter = "all" | "unmapped" | "shown-unmapped";

const MAPPING_FILTERS: { label: string; value: MappingFilter }[] = [
  { label: "All", value: "all" },
  { label: "Unmapped", value: "unmapped" },
  { label: "Shown without mapping", value: "shown-unmapped" },
];

const matchesFilter = (entry: WallboxEntry, filter: MappingFilter): boolean => {
  if (filter === "unmapped") return !entry.eround;
  if (filter === "shown-unmapped") return entry.integrated && !entry.eround;
  return true;
};

/**
 * US4: wallbox catalogue management. Lists all entries (incl. non-integrated),
 * supports add / edit / delete, and each entry's eRound mapping (SOPS-169).
 */
const WallboxCataloguePage: React.FC = () => {
  const toast = useRef<Toast>(null);
  const { entries, loading, error, reload, create, update, remove } =
    useWallboxCatalogue(toast);
  const [importVisible, setImportVisible] = useState(false);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editing, setEditing] = useState<WallboxEntry | null>(null);
  const [filter, setFilter] = useState<MappingFilter>("all");
  const shown = entries.filter((entry) => matchesFilter(entry, filter));

  const openAdd = () => {
    setEditing(null);
    setDialogVisible(true);
  };
  const openEdit = (entry: WallboxEntry) => {
    setEditing(entry);
    setDialogVisible(true);
  };

  const confirmDelete = (entry: WallboxEntry) => {
    confirmDialog({
      message: `Delete "${entry.manufacturer} — ${entry.model_string}"? Prospects that already use this model keep their stored value.`,
      header: "Delete catalogue entry",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => remove(entry.id),
    });
  };

  const integratedBody = (entry: WallboxEntry) =>
    entry.integrated ? (
      <Tag severity="success" value="Shown to customers" />
    ) : (
      <Tag severity="warning" value="Hidden" />
    );

  const eroundManufacturerBody = (entry: WallboxEntry) =>
    entry.eround ? entry.eround.manufacturer : <Tag severity="warning" value="Not mapped" />;

  const actionsBody = (entry: WallboxEntry) => (
    <div className="flex gap-2">
      <Button
        icon="pi pi-pencil"
        rounded
        text
        aria-label="Edit"
        onClick={() => openEdit(entry)}
      />
      <Button
        icon="pi pi-trash"
        rounded
        text
        severity="danger"
        aria-label="Delete"
        onClick={() => confirmDelete(entry)}
      />
    </div>
  );

  return (
    <div className="card onboarding-page">
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="flex align-items-center justify-content-between mb-3">
        <h2 className="m-0">Wallbox Catalogue</h2>
        <div className="flex gap-2">
          <Button
            label="Download catalogue (CSV)"
            icon="pi pi-download"
            outlined
            disabled={entries.length === 0}
            onClick={() => downloadCatalogueCsv(entries)}
          />
          <Button
            label="Import eRound mappings"
            icon="pi pi-upload"
            outlined
            onClick={() => setImportVisible(true)}
          />
          <Button label="Add entry" icon="pi pi-plus" onClick={openAdd} />
        </div>
      </div>

      {error && (
        <Message
          severity="error"
          text="The catalogue could not be loaded."
          className="mb-3 w-full"
        />
      )}

      <SelectButton
        className="mb-3"
        value={filter}
        options={MAPPING_FILTERS}
        onChange={(e) => e.value && setFilter(e.value)}
        allowEmpty={false}
      />

      <DataTable
        value={shown}
        loading={loading}
        paginator
        rows={10}
        rowsPerPageOptions={[10, 25, 50]}
        emptyMessage="No catalogue entries yet."
      >
        <Column field="manufacturer" header="Manufacturer" sortable />
        <Column field="model_string" header="Model" sortable />
        <Column header="eRound manufacturer" body={eroundManufacturerBody} />
        <Column header="eRound model" body={(entry: WallboxEntry) => entry.eround?.model ?? ""} />
        <Column
          header="Charging points"
          body={(entry: WallboxEntry) => entry.eround?.charging_points ?? ""}
        />
        <Column header="Visibility" body={integratedBody} />
        <Column header="" body={actionsBody} style={{ width: "8rem" }} />
      </DataTable>

      <ImportMappingsDialog
        visible={importVisible}
        entries={entries}
        onHide={() => setImportVisible(false)}
        onApplied={() => void reload()}
      />

      <WallboxEntryDialog
        visible={dialogVisible}
        entry={editing}
        onHide={() => setDialogVisible(false)}
        onSubmit={(values) =>
          editing ? update(editing.id, values) : create(values)
        }
      />
    </div>
  );
};

export default WallboxCataloguePage;
